import React, { useMemo, useState } from 'react';
import { useProfileParams, isAISummariesAccessible } from '../../../../../../utils/common';
import { useGetBusinessPerformanceDetailsQuery } from '../../../../../../redux/services/businessPerformaceAPI';
import styles from './ProductivityPerBuyerTile.module.scss';
import KPITile from '../../../../../common/KPITile/KPITile';
import KPIDetailTile from '../../../../../common/KPIDetailTile/KPIDetailTile';
import Benchmark from '../../../../../common/Benchmark/Benchmark';
import Tooltip from '../../../../../common/Tooltip/Tooltip';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../../common/Icon/Icon';
import { useAppSelector } from '../../../../../../redux/hooks';
import GraphCard from '../../../../../common/GraphCard/GraphCard';
import GenericTable from '../../../../../common/GenericTable/GenericTable';
import InsightAI from '../../../../../common/InsightAI/InsightAI';
import Skeleton from '../../../../../common/Skeleton/Skeleton';
import isEmpty from 'lodash/isEmpty';
import { useGetAGCAISummaryQuery } from '../../../../../../redux/services/agcAIAPI';
import {
  CurrencyFormatter,
  CurrencyInputFormatter,
} from '../../../../../../utils/currencyFormatter';
import { NumberFormatter } from '../../../../../../utils/NumberFormatter';
import ViewModeSwitcher, {
  ViewMode,
} from '../../../../../common/ViewModeSwitcher/ViewModeSwitcher';

interface SummaryProp {
  summary?: any;
  loading?: boolean;
  affiliateCode?: string;
}

export const ProductivityPerBuyerTile: React.FC<SummaryProp> = ({
  summary,
  loading = false,
  affiliateCode = '010',
}) => {
  const { t } = useTranslation();
  const benchmark = summary?.benchmark;
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);

  const demandSales = summary?.metricSourceData.find(
    (v: { key: string }) => v.key === 'product_demand_sales'
  );
  const demandSalesValue = demandSales?.value ?? null;
  const demandSalesCurrency = demandSales?.currency;
  const buyerCount = summary?.metricSourceData.find(
    (v: { key: string }) => v.key === 'downline_buyer_count'
  );
  const buyerCountValue = buyerCount?.value ?? null;
  const buyerSalesValue = summary?.kpiValue ?? null;
  const buyerSalesCurrency = summary?.kpiCurrency;

  return (
    <KPITile
      title={t('kpiTitles.buyerProductivity')}
      dataTestId="productivity-per-buyer-tile"
      loading={loading}
    >
      <div className={styles.tile_content}>
        <div className={styles.values_wrapper}>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>{t('kpiLabels.prodDemandSales')}</div>
            <div className={styles.value_content}>
              {demandSalesValue !== null && demandSalesValue !== undefined
                ? CurrencyInputFormatter(demandSalesCurrency, demandSalesValue, true)
                : '--'}
            </div>
          </div>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>/</div>
            <div className={styles.value_content}></div>
          </div>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>{t('kpiLabels.buyerCount')}</div>
            <div className={styles.value_content}>
              {summary?.metricSourceData?.[1]?.value
                ? NumberFormatter(affiliateCode, buyerCountValue)
                : '--'}
            </div>
          </div>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>=</div>
            <div className={styles.value_content}></div>
          </div>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>{t('kpiLabels.dollarContributor')}</div>
            <div className={`${styles.value_content} ${styles.value_highlight}`}>
              {buyerSalesValue !== null && buyerSalesValue !== undefined
                ? CurrencyInputFormatter(buyerSalesCurrency, buyerSalesValue, true)
                : '--'}
            </div>
          </div>
        </div>

        <div className={styles.bar_wrapper}>
          {
            <Benchmark
              minValue={benchmark?.min}
              maxValue={benchmark?.max}
              averageValue={benchmark?.marketAverage}
              medianValue={benchmark?.median}
              currentBenchmarkValue={summary?.kpiValue}
              quartileLine={[benchmark?.lowerQuartile ?? 0, benchmark?.upperQuartile ?? 0]}
              benchmarkDataType={summary?.benchmarkDataType}
              currency={summary?.kpiCurrency}
              isPercentValue={false}
            />
          }
          <div className={styles.bar_footer}>
            {t('market_average')}{' '}
            <span className={styles.bar_footer_value}>
              {String(CurrencyFormatter(summary?.kpiCurrency, benchmark?.marketAverage)).replace(
                /\.00$/,
                ''
              )}
            </span>
            <span>
              <Tooltip
                content={<span>{t('kpiTooltips.productivityPerBuyer')}</span>}
                width="250"
                height="auto"
                bottom="30px"
                left="685%"
              >
                <span>
                  <Icon
                    name="info_icon"
                    size={16}
                    color={currentTheme === 'light' ? '#707070' : '#AEAEB2'}
                  />
                </span>
              </Tooltip>
            </span>
          </div>
        </div>
      </div>
    </KPITile>
  );
};

export const ProductivityPerBuyerDetail: React.FC<{
  onClose?: () => void;
}> = ({ onClose }) => {
  const { affiliateCode, aboNumber } = useProfileParams();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('left');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('productivity');

  const { data: kpiDetailsResponse, isLoading } = useGetBusinessPerformanceDetailsQuery(
    {
      affiliateCode,
      aboNumber,
      payload: { kpis: [{ code: 'productivityPerBuyer' }] } as any,
    },
    { skip: !onClose }
  );

  const { data: agcAISummary, isLoading: isLoadingAiSummary } = useGetAGCAISummaryQuery(
    {
      affiliateCode,
      aboNumber,
    },
    {
      skip: Number(affiliateCode) === 0 || aboNumber === 0,
      refetchOnMountOrArgChange: true,
    }
  );

  const detailData = kpiDetailsResponse?.body?.kpis?.[0] || {};
  const detailView = detailData?.detail || {};

  // Extract chart data from productivityView and countView
  const productivityViewCharts = detailView?.productivityView?.chart || [];
  const countViewCharts = detailView?.countView?.chart || [];

  // Extract line chart data from productivityView
  const productivityLineChart = productivityViewCharts.find(
    (chart: any) => chart.chartType === 'lineChart'
  );
  const productivityLineChartData = productivityLineChart?.chartDetail?.lineChartDetail;

  // Extract line chart data from countView
  const countLineChart = countViewCharts.find(
    (chart: any) => chart.chartType === 'lineChart'
  );
  const countLineChartData = countLineChart?.chartDetail?.lineChartDetail;

  const productivityTableChart = productivityViewCharts.find(
    (chart: any) => chart.chartType === 'table'
  );
  const productivityTableData = productivityTableChart?.chartDetail?.tableDetail;

  const countTableChart = countViewCharts.find(
    (chart: any) => chart.chartType === 'table'
  );
  const countTableData = countTableChart?.chartDetail?.tableDetail;

  const activeTableData =
    selectedPeriod === 'productivity' ? productivityTableData : countTableData;

  const activeLineChartData =
    selectedPeriod === 'productivity' ? productivityLineChartData : countLineChartData;

  const activeChartSummary =
    selectedPeriod === 'productivity'
      ? productivityLineChart?.chartSummary
      : countLineChart?.chartSummary;

  const periodItems = useMemo(
    () => [
      { code: 'productivity', label: t('productivity') },
      { code: 'buyerCount', label: t('buyerCount') },
    ],
    [t]
  );

  if (isLoading) return <Skeleton height="400px" />;

  const aiContent =
    agcAISummary?.body?.summary?.length > 300
      ? `${agcAISummary?.body?.summary?.slice(0, 300)}...`
      : agcAISummary?.body?.summary;
  const headerContent = (
    <div className={styles.header_content}>
      <div className={styles.tile_header}>
        <div className={styles.title}>
          {periodItems.find((item) => item.code === selectedPeriod)?.label}
        </div>
        <div className={styles.desc}>{t('kpiDescription.productivityPerBuyer')}</div>
      </div>
      <ViewModeSwitcher
        mode={viewMode}
        onChangeMode={setViewMode}
        disabled={false}
        showSwitch={true}
        selectedTimeRangeCode={selectedPeriod}
        onSelectTimeRange={setSelectedPeriod}
        timeRanges={periodItems}
      />
    </div>
  );

  const leftContent = (
    <div className={styles.left_content}>
      {viewMode === 'left' ? (
        isEmpty(activeLineChartData) ? (
          <div className={styles.no_data}>{t('noDataAvailable')}</div>
        ) : (
          <GraphCard
            lineChartData={activeLineChartData}
            currency={detailData?.kpiCurrency || 'USD'}
            chartSummaryData={activeChartSummary}
          />
        )
      ) : isEmpty(activeTableData) ? (
        <div className={styles.no_data}>{t('noDataAvailable')}</div>
      ) : (
        <GenericTable
          tableData={activeTableData}
          kpiCode="productivityPerBuyer"
          currency={detailData?.kpiCurrency || 'USD'}
          isOnlyNegativeHighlight
          highlightRows={[0]}
        />
      )}
    </div>
  );

  const rightContent = isAISummariesAccessible() ? (
    isLoadingAiSummary ? (
      <Skeleton height="200px" />
    ) : (
      <InsightAI
        updateDate=""
        content={aiContent}
        title={t('ai_summary')}
        showDropdown={false}
        isDropdownEnabled={true}
        flowerIconSize={24}
        titleSize={14}
      />
    )
  ) : null;

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="productivity-per-buyer-detail"
    />
  );
};

export default ProductivityPerBuyerTile;
